/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/offlineManager.ts
 * PURPOSE: Business logic service layer implementation
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import Dexie, { Table } from 'dexie'
import { buildApiUrl } from '../config/enums'

// =================== Database Schema ===================

interface AIResultCache {
  id?: number
  key: string
  data: any
  timestamp: Date
  synced: boolean
}

interface WorkflowCache {
  id?: number
  workflowId: string
  data: any
  status: string
  timestamp: Date
  synced: boolean
}

interface AttendanceQueue {
  id?: number
  action: 'clock_in' | 'clock_out'
  data: any
  timestamp: Date
  retries: number
  lastError?: string
  synced: boolean
}

interface SyncLog {
  id?: number
  type: string
  action: string
  timestamp: Date
  success: boolean
  details?: any
}

// =================== Offline Database ===================

class OfflineDatabase extends Dexie {
  aiResults!: Table<AIResultCache>
  workflows!: Table<WorkflowCache>
  attendanceQueue!: Table<AttendanceQueue>
  syncLogs!: Table<SyncLog>

  constructor() {
    super('NQHBotOfflineDB')
    
    this.version(1).stores({
      aiResults: '++id, key, timestamp, synced',
      workflows: '++id, workflowId, status, timestamp, synced',
      attendanceQueue: '++id, action, timestamp, synced',
      syncLogs: '++id, type, timestamp, success'
    })
  }
}

// =================== Offline Manager Class ===================

class OfflineManager {
  private db: OfflineDatabase
  private syncInProgress = false
  private syncInterval: number | null = null
  private readonly MAX_RETRIES = 3
  private readonly SYNC_INTERVAL = 30000 // 30 seconds
  private readonly CACHE_EXPIRY = 24 * 60 * 60 * 1000 // 24 hours

  constructor() {
    this.db = new OfflineDatabase()
    this.initializeSync()
    this.cleanupOldData()
  }

  // =================== AI Results Management ===================

  async saveAIResult(key: string, data: any): Promise<void> {
    try {
      await this.db.aiResults.add({
        key,
        data,
        timestamp: new Date(),
        synced: false
      })
    } catch (error) {

    }
  }

  async getAIResult(key: string): Promise<any | null> {
    try {
      const result = await this.db.aiResults
        .where('key')
        .equals(key)
        .first()
      
      if (result && this.isDataFresh(result.timestamp)) {
        return result.data
      }
      
      return null
    } catch (error) {

      return null
    }
  }

  async getAllAIResults(): Promise<Array<{ key: string; data: any }>> {
    try {
      const results = await this.db.aiResults
        .where('timestamp')
        .above(new Date(Date.now() - this.CACHE_EXPIRY))
        .toArray()
      
      return results.map(r => ({ key: r.key, data: r.data }))
    } catch (error) {

      return []
    }
  }

  // =================== Workflow Management ===================

  async saveWorkflow(workflowId: string, data: any): Promise<void> {
    try {
      const existing = await this.db.workflows
        .where('workflowId')
        .equals(workflowId)
        .first()
      
      if (existing) {
        await this.db.workflows.update(existing.id!, {
          data,
          status: data.status,
          timestamp: new Date(),
          synced: false
        })
      } else {
        await this.db.workflows.add({
          workflowId,
          data,
          status: data.status,
          timestamp: new Date(),
          synced: false
        })
      }
    } catch (error) {

    }
  }

  async getWorkflow(workflowId: string): Promise<any | null> {
    try {
      const workflow = await this.db.workflows
        .where('workflowId')
        .equals(workflowId)
        .first()
      
      return workflow?.data || null
    } catch (error) {

      return null
    }
  }

  // =================== Attendance Queue Management ===================

  async queueAttendanceAction(action: 'clock_in' | 'clock_out', data: any): Promise<void> {
    try {
      await this.db.attendanceQueue.add({
        action,
        data,
        timestamp: new Date(),
        retries: 0,
        synced: false
      })
      
      // Trigger immediate sync attempt
      this.syncQueue()
    } catch (error) {

    }
  }

  async getQueuedAttendance(): Promise<AttendanceQueue[]> {
    try {
      return await this.db.attendanceQueue
        .where('synced')
        .equals(false)
        .and(item => item.retries < this.MAX_RETRIES)
        .toArray()
    } catch (error) {

      return []
    }
  }

  // =================== Sync Management ===================

  private initializeSync(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => {

      this.startSync()
    })
    
    window.addEventListener('offline', () => {

      this.stopSync()
    })
    
    // Start sync if online
    if (navigator.onLine) {
      this.startSync()
    }
  }

  private startSync(): void {
    if (this.syncInterval) return
    
    // Initial sync
    this.syncAll()
    
    // Set up periodic sync
    this.syncInterval = window.setInterval(() => {
      this.syncAll()
    }, this.SYNC_INTERVAL)
  }

  private stopSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
      this.syncInterval = null
    }
  }

  async syncAll(): Promise<void> {
    if (!navigator.onLine || this.syncInProgress) return
    
    this.syncInProgress = true
    
    try {
      await Promise.all([
        this.syncQueue(),
        this.syncWorkflows(),
        this.syncAIResults()
      ])
      
      await this.logSync('full_sync', 'complete', true)
    } catch (error) {

      await this.logSync('full_sync', 'failed', false, error)
    } finally {
      this.syncInProgress = false
    }
  }

  private async syncQueue(): Promise<void> {
    const queued = await this.getQueuedAttendance()
    
    for (const item of queued) {
      try {
        // Send to backend
        // Use buildApiUrl to ensure proper /api/v1 prefix
        const endpoint = '/attendance/' + item.action.replace('_', '-');
        const response = await fetch(buildApiUrl(endpoint), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(item.data)
        })
        
        if (response.ok) {
          // Mark as synced
          await this.db.attendanceQueue.update(item.id!, { synced: true })
          await this.logSync('attendance', item.action, true)
        } else {
          // Increment retry count
          await this.db.attendanceQueue.update(item.id!, {
            retries: item.retries + 1,
            lastError: `HTTP ${response.status}`
          })
        }
      } catch (error) {
        // Network error - will retry later
        await this.db.attendanceQueue.update(item.id!, {
          retries: item.retries + 1,
          lastError: String(error)
        })
      }
    }
  }

  private async syncWorkflows(): Promise<void> {
    const unsynced = await this.db.workflows
      .where('synced')
      .equals(false)
      .toArray()
    
    for (const workflow of unsynced) {
      try {
        // Sync with backend
        const response = await fetch(`/api/v1/bmp/workflows/${workflow.workflowId}/sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(workflow.data)
        })
        
        if (response.ok) {
          await this.db.workflows.update(workflow.id!, { synced: true })
        }
      } catch (error) {

      }
    }
  }

  private async syncAIResults(): Promise<void> {
    // AI results are read-only cache, no need to sync back
    // Just clean up old entries
    await this.cleanupOldData()
  }

  // =================== Utility Methods ===================

  private isDataFresh(timestamp: Date): boolean {
    return Date.now() - timestamp.getTime() < this.CACHE_EXPIRY
  }

  private async cleanupOldData(): Promise<void> {
    try {
      const cutoff = new Date(Date.now() - this.CACHE_EXPIRY)
      
      // Clean old AI results
      await this.db.aiResults
        .where('timestamp')
        .below(cutoff)
        .delete()
      
      // Clean synced attendance queue items
      await this.db.attendanceQueue
        .where('synced')
        .equals(true)
        .delete()
      
      // Clean old sync logs (keep last 100)
      const oldLogs = await this.db.syncLogs
        .orderBy('timestamp')
        .reverse()
        .offset(100)
        .toArray()
      
      if (oldLogs.length > 0) {
        await this.db.syncLogs.bulkDelete(oldLogs.map(l => l.id!))
      }
    } catch (error) {

    }
  }

  private async logSync(type: string, action: string, success: boolean, details?: any): Promise<void> {
    try {
      await this.db.syncLogs.add({
        type,
        action,
        timestamp: new Date(),
        success,
        details
      })
    } catch (error) {

    }
  }

  // =================== Public API ===================

  async clearAll(): Promise<void> {
    await this.db.transaction('rw', this.db.aiResults, this.db.workflows, this.db.attendanceQueue, async () => {
      await this.db.aiResults.clear()
      await this.db.workflows.clear()
      await this.db.attendanceQueue.clear()
    })
  }

  async getStats(): Promise<any> {
    const [aiCount, workflowCount, queueCount, lastSync] = await Promise.all([
      this.db.aiResults.count(),
      this.db.workflows.count(),
      this.db.attendanceQueue.where('synced').equals(false).count(),
      this.db.syncLogs.orderBy('timestamp').reverse().first()
    ])
    
    return {
      cachedAIResults: aiCount,
      cachedWorkflows: workflowCount,
      queuedActions: queueCount,
      lastSyncTime: lastSync?.timestamp || null,
      lastSyncSuccess: lastSync?.success || false,
      isOnline: navigator.onLine,
      syncInProgress: this.syncInProgress
    }
  }

}

// =================== Export Singleton ===================

export const offlineManager = new OfflineManager()
// ISSUE-027: removed module-level offlineManager.registerServiceWorker() auto-call.
// SW registration is handled by VitePWA registerType:'autoUpdate' virtual module.
// The old call was registering '/service-worker.js' (wrong path, actual SW is '/sw.js'),
// causing GET /service-worker.js → 404 on every page load.